import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ComposicaoProposta } from '@/constants/planosCNPJ';

/**
 * As frases de benefício dos produtos que estão na composição da proposta.
 *
 * O texto é escrito pelo Douglas em Configurações → Produtos → aba Geral
 * (`produtos.beneficio_proposta`) e aparece nas mensagens de proposta. Aqui só
 * resolvemos quais produtos a composição usa e devolvemos as frases, sem
 * repetir a mesma duas vezes.
 *
 * Produto sem texto não entra — a lista é exatamente o que foi escrito, nada de
 * frase genérica preenchendo espaço.
 */
export function useBeneficiosDaComposicao(composicao?: ComposicaoProposta | null): string[] {
  const { data: produtos = [] } = useQuery({
    queryKey: ['produtos-beneficio-proposta'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('produtos')
        .select('id, beneficio_proposta')
        .not('beneficio_proposta', 'is', null);
      if (error) throw error;
      return data ?? [];
    },
  });

  return useMemo(() => {
    if (!composicao?.linhas?.length || produtos.length === 0) return [];

    const textoPorProduto = new Map<string, string>();
    for (const p of produtos) {
      const texto = String(p.beneficio_proposta ?? '').trim();
      if (texto) textoPorProduto.set(p.id, texto);
    }

    // Ordem de aparição na composição, sem repetir frase igual
    const frases: string[] = [];
    for (const linha of composicao.linhas) {
      const produtoId = linha.plano?.produtoPedidoId;
      if (!produtoId) continue;
      const texto = textoPorProduto.get(produtoId);
      if (texto && !frases.includes(texto)) frases.push(texto);
    }
    return frases;
  }, [composicao, produtos]);
}
